import { Component, Input, OnChanges, OnInit } from "@angular/core";
import { IVTableColumn } from "@app/directives/_layout/v-table/_models/IVTableColumn";
import { IVTableRow } from "@app/directives/_layout/v-table/_models/IVTableRow";
import { VTableService } from "@app/directives/_layout/v-table/_services/v-table.service";
import { SortDirection } from "@app/enum/SortDirection";
import { IDeviceMessageQueryOptions } from "@app/main/modules/foundation/_models/IDeviceMessageQueryOptions";
import { DeviceMessageService } from "@app/main/modules/foundation/_services/deviceMessage.service";
import { TranslationService } from "@app/services/translation.service";
import { DeviceCommunicationMethod, DeviceMessageDirection, ThirdPartyMessagingProvider } from "@enums/deviceMessage/DeviceCommunicationMethod";
import { DeviceCommunicationType } from "@enums/deviceMessage/DeviceCommunicationType";
import { DeviceMessageStatus } from "@enums/deviceMessage/DeviceMessageStatus";
import { IDeviceCommand, IDeviceCommunicationCommand, IDeviceMessageExtended } from "@interfaces/IDeviceMessage";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { map, mergeMap, take, tap } from "rxjs/operators";
import "./commandSender.component.scss";

@UntilDestroy()
@Component({
    selector: "foundation-command-sender",
    template: require("./commandSender.component.html")
})

export class CommandSenderComponent implements OnInit, OnChanges {

    // bindings
    @Input() unitSerial: string;
    @Input() isPeripheralCommand: boolean;

    // component
    icon: string;
    label: string;
    loading: boolean = false;
    deviceMessages: IDeviceMessageExtended[] = [];
    deviceCommunicationCommands: IDeviceCommunicationCommand[] = []
    selectedDeviceCommunicationCommand: IDeviceCommunicationCommand;
    commandAdditionalValue: string;
    deviceCommand: IDeviceCommand;

    deviceMessageDirection: typeof DeviceMessageDirection = DeviceMessageDirection;
    deviceCommunicationMethod: typeof DeviceCommunicationMethod = DeviceCommunicationMethod;
    deviceMessageStatus: typeof DeviceMessageStatus = DeviceMessageStatus;
    thirdPartyMessagingProvider: typeof ThirdPartyMessagingProvider = ThirdPartyMessagingProvider;

    tableColumns: IVTableColumn<IDeviceMessageExtended>[];
    tableRows: IVTableRow[];

    showSendCommandSidebar: boolean;
    additionalValueInfo: string;

    valueSelectorRequired: boolean;

    constructor(
        private readonly vTableService: VTableService,
        private readonly deviceMessageService: DeviceMessageService,
        private readonly translationService: TranslationService
    ) {
    }

    ngOnInit(): void {
        this.getData();
        this.createTable();
    }

    ngOnChanges(): void {
        if (!this.unitSerial) {
            console.warn("No serial given to command sender");
            return;
        }

        this.getDeviceCommunicationTypes();
    }

    sendCommand(): void {
        this.deviceMessageService.sendDeviceCommunicationCommands(this.unitSerial, this.createDeviceCommand()).pipe(take(1), tap(deviceMessage => {
            deviceMessage.statusString = DeviceMessageStatus[deviceMessage.Status];
            this.deviceMessages.push(deviceMessage);
            this.createTableRows();
            this.toggleSidebar();
        })).subscribe();
    }

    onCommandTypeChange(): void {
        this.setValueSelectorAndInfo();
    }

    onValueChange($event: string): void {
        this.commandAdditionalValue = $event;
    }

    toggleSidebar(): void {
        this.showSendCommandSidebar = !this.showSendCommandSidebar
    }

    private setValueSelectorAndInfo():  void {
        switch (this.selectedDeviceCommunicationCommand.Item2) {
            case DeviceCommunicationType.TelematicsMode:
            case DeviceCommunicationType.LoggingLevel:              
                this.additionalValueInfo = `0 = ${this.translationService.translate("Common.Disabled")}, 1 = ${this.translationService.translate("Common.Enabled")}`;
                this.valueSelectorRequired = true;
                break;
            case DeviceCommunicationType.CameraReboot:
            case DeviceCommunicationType.RemovePendingVideoRequests:
                this.additionalValueInfo = "";
                this.valueSelectorRequired = false;
                break;
            default:
                this.additionalValueInfo = "";
                this.valueSelectorRequired = true;
                break;
        }
    }

    private createDeviceCommand(): IDeviceCommand {
        return {
            CommandValue: this.commandAdditionalValue,
            CommunicationMethod: this.selectedDeviceCommunicationCommand.Item1,
            CommunicationType: this.selectedDeviceCommunicationCommand.Item2
        };
    }

    private getDeviceCommunicationTypes(): void {

        this.deviceMessageService.getDeviceCommunicationCommands(this.unitSerial, this.isPeripheralCommand).pipe(take(1)).subscribe(deviceCommunicationCommands => {
            if (this.isPeripheralCommand) {
                deviceCommunicationCommands = deviceCommunicationCommands.filter(command =>
                    command.Item2 != DeviceCommunicationType.Custom && 
                    command.Item2 != DeviceCommunicationType.GetLogFile &&
                    command.Item2 != DeviceCommunicationType.GenericFileUpload); 
            }

            this.deviceCommunicationCommands = deviceCommunicationCommands;
            this.deviceCommunicationCommands.forEach(type => { type.DisplayName = DeviceCommunicationType[type.Item2].replace(/([A-Z])/g, ' $1').trim() });
            this.selectedDeviceCommunicationCommand = deviceCommunicationCommands.firstOrDefault();
            this.setValueSelectorAndInfo();
        });
    }

    private getData(): void {
        this.loading = true;

        const getParams: IDeviceMessageQueryOptions = this.isPeripheralCommand ?
            { PeripheralSerial: this.unitSerial } :
            { Serial: this.unitSerial };

        this.deviceMessageService.getAllDeviceMessages(getParams).pipe( 
            untilDestroyed(this),
            map(deviceMessages => {
                this.deviceMessages = deviceMessages;

                this.deviceMessages.forEach(message => {
                    message.directionString = DeviceMessageDirection[message.Direction];
                    message.deviceCommunicationMethodString = DeviceCommunicationMethod[message.DeviceCommunicationMethod];
                    message.statusString = DeviceMessageStatus[message.Status];
                    message.thirdPartyMessagingProviderString = ThirdPartyMessagingProvider[message.ThirdPartyMessagingProvider];
                });

                this.createTableRows();
                this.loading = false;
            })).subscribe();
    }
    
    private createTable(): void {
        this.tableColumns = [
            {
                header: this.vTableService.createDateTableHeader({ tKey: "Common.Time", sorted: true, sortDirection: SortDirection.Descending }),
                createCellFunction: message => this.vTableService.createDateCell(message.TimestampCreated, { dateTime: true })
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Status", width: 150 }),
                createCellFunction: message => this.vTableService.createTableCell(message.statusString)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "CommandSender.Message" }),
                createCellFunction: message => this.vTableService.createTableCell(message.Message)
            }
        ]
    }

    private createTableRows(): void {
        this.tableRows = this.vTableService.createTableRows(this.deviceMessages, this.tableColumns);
    }
}
